/**
 * MediaTrashPage — Phase B édito (2026-09-21)
 *
 * Deux onglets :
 *   - « Analyse »  : compteurs (utilisés / social / orphelins / corbeille) + liste dry-run
 *                    des orphelins + bouton « Mettre en corbeille » (confirmation obligatoire).
 *   - « Corbeille » : liste des médias en corbeille, jours restants, restauration, purge
 *                     physique (dry-run par défaut, confirm=true pour agir).
 *
 * Additif : n'écrase rien, ne touche pas la médiathèque existante. Utilisé
 * uniquement par admin. Aucune purge automatique.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  getMediaUsageSummary,
  getMediaOrphans,
  moveMediaToTrash,
  getMediaTrash,
  restoreMediaFromTrash,
  purgeMediaTrash,
} from "../lib/api";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  AlertTriangle,
  Archive,
  ArchiveRestore,
  Loader2,
  RefreshCw,
  Search,
  Trash2,
} from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return String(iso);
  }
}

function StatCard({ label, value, tone = "default", testId }) {
  const toneClasses = {
    default: "bg-white border-border",
    accent: "bg-primary/5 border-primary/40",
    warn: "bg-amber-50 border-amber-200",
    danger: "bg-red-50 border-red-200",
  };
  return (
    <div
      className={`rounded-lg border p-4 ${toneClasses[tone] || toneClasses.default}`}
      data-testid={testId}
    >
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 font-heading text-3xl text-primary">{value ?? "—"}</div>
    </div>
  );
}

export default function MediaTrashPage() {
  const [tab, setTab] = useState("analyse");

  // Analyse tab
  const [summary, setSummary] = useState(null);
  const [orphans, setOrphans] = useState([]);
  const [orphansTotal, setOrphansTotal] = useState(0);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [selectedOrphans, setSelectedOrphans] = useState(() => new Set());
  const [openMoveConfirm, setOpenMoveConfirm] = useState(false);
  const [moving, setMoving] = useState(false);

  // Trash tab
  const [trashItems, setTrashItems] = useState([]);
  const [trashTotal, setTrashTotal] = useState(0);
  const [loadingTrash, setLoadingTrash] = useState(false);
  const [selectedTrash, setSelectedTrash] = useState(() => new Set());
  const [openPurgeConfirm, setOpenPurgeConfirm] = useState(false);
  const [purgeResult, setPurgeResult] = useState(null);
  const [purging, setPurging] = useState(false);

  const loadAnalysis = useCallback(async () => {
    setLoadingAnalysis(true);
    try {
      const [s, o] = await Promise.all([
        getMediaUsageSummary(),
        getMediaOrphans({ limit: 500, skip: 0 }),
      ]);
      setSummary(s.data);
      setOrphans(o.data?.items || []);
      setOrphansTotal(o.data?.total || 0);
      setSelectedOrphans(new Set());
    } catch (e) {
      toast.error("Impossible de charger l'analyse : " + (e?.response?.data?.detail || e?.message || "erreur"));
    } finally {
      setLoadingAnalysis(false);
    }
  }, []);

  const loadTrash = useCallback(async () => {
    setLoadingTrash(true);
    try {
      const r = await getMediaTrash({ limit: 500, skip: 0 });
      setTrashItems(r.data?.items || []);
      setTrashTotal(r.data?.total || 0);
      setSelectedTrash(new Set());
    } catch (e) {
      toast.error("Impossible de charger la corbeille : " + (e?.response?.data?.detail || e?.message || "erreur"));
    } finally {
      setLoadingTrash(false);
    }
  }, []);

  useEffect(() => {
    loadAnalysis();
    loadTrash();
  }, [loadAnalysis, loadTrash]);

  const toggleOrphan = (id) => {
    setSelectedOrphans((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const toggleAllOrphans = () => {
    if (selectedOrphans.size === orphans.length) setSelectedOrphans(new Set());
    else setSelectedOrphans(new Set(orphans.map((o) => o.id)));
  };

  const toggleTrash = (id) => {
    setSelectedTrash((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const doMoveToTrash = async () => {
    setMoving(true);
    try {
      const media_ids = Array.from(selectedOrphans);
      const payload = media_ids.length > 0
        ? { media_ids, confirm: true, retention_days: 30 }
        : { confirm: true, retention_days: 30 };
      const r = await moveMediaToTrash(payload);
      const moved = r.data?.moved_count ?? 0;
      const rejected = r.data?.rejected_count ?? 0;
      toast.success(`Mise en corbeille : ${moved} déplacé(s), ${rejected} refusé(s).`);
      setOpenMoveConfirm(false);
      await loadAnalysis();
      await loadTrash();
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e?.message || "unknown"));
    } finally {
      setMoving(false);
    }
  };

  const doRestore = async (ids) => {
    if (!ids || ids.length === 0) return;
    try {
      const r = await restoreMediaFromTrash(ids);
      toast.success(`${r.data?.restored_count ?? 0} média(s) restauré(s).`);
      await loadAnalysis();
      await loadTrash();
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e?.message || "unknown"));
    }
  };

  const doPurgeDryRun = async () => {
    const media_ids = Array.from(selectedTrash);
    if (media_ids.length === 0) {
      toast.error("Sélectionnez au moins un média à purger.");
      return;
    }
    try {
      const r = await purgeMediaTrash({ media_ids, dry_run: true, confirm: false });
      setPurgeResult(r.data);
      setOpenPurgeConfirm(true);
    } catch (e) {
      toast.error("Erreur dry-run : " + (e?.response?.data?.detail || e?.message || "unknown"));
    }
  };

  const doPurgeConfirmed = async () => {
    if (!purgeResult) return;
    setPurging(true);
    try {
      const r = await purgeMediaTrash({
        media_ids: purgeResult.eligible || [],
        dry_run: false,
        confirm: true,
      });
      toast.success(`${r.data?.purged_count ?? 0} média(s) purgé(s) définitivement.`);
      setOpenPurgeConfirm(false);
      setPurgeResult(null);
      await loadAnalysis();
      await loadTrash();
    } catch (e) {
      toast.error("Erreur purge : " + (e?.response?.data?.detail || e?.message || "unknown"));
    } finally {
      setPurging(false);
    }
  };

  const canMove = orphans.length > 0;
  const selectedOrphanCount = selectedOrphans.size;
  const targetCount = selectedOrphanCount || orphans.length;

  const trashSelectionCount = selectedTrash.size;
  const trashEligibleCount = useMemo(
    () => trashItems.filter((t) => t.purge_eligible && selectedTrash.has(t.id)).length,
    [trashItems, selectedTrash]
  );

  return (
    <div data-testid="media-trash-page" className="max-w-6xl">
      <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl text-primary flex items-center gap-3">
            <Archive className="h-7 w-7" />
            Corbeille & analyse médias
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Détecte les médias vraiment orphelins (non utilisés sur ITERCATUS, hors archive sociale)
            et permet une mise en corbeille sécurisée avec 30&nbsp;jours de rétention.
            Aucune purge automatique&nbsp;: la suppression physique reste manuelle et confirmée.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => {
            loadAnalysis();
            loadTrash();
          }}
          data-testid="media-trash-refresh-btn"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Rafraîchir
        </Button>
      </header>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="media-trash-tabs">
          <TabsTrigger value="analyse" data-testid="media-trash-tab-analyse">
            <Search className="h-4 w-4 mr-2" />
            Analyse (dry-run)
          </TabsTrigger>
          <TabsTrigger value="corbeille" data-testid="media-trash-tab-corbeille">
            <Trash2 className="h-4 w-4 mr-2" />
            Corbeille ({summary?.trashed ?? trashTotal ?? 0})
          </TabsTrigger>
        </TabsList>

        {/* -------- Analyse -------- */}
        <TabsContent value="analyse" className="mt-6 space-y-6">
          <section className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="media-usage-summary">
            <StatCard label="Total médias" value={summary?.total_media} testId="stat-total" />
            <StatCard label="Utilisés" value={summary?.used} tone="accent" testId="stat-used" />
            <StatCard label="Archive sociale" value={summary?.social_reserved} testId="stat-social" />
            <StatCard label="Orphelins" value={summary?.orphans} tone="warn" testId="stat-orphans" />
            <StatCard label="En corbeille" value={summary?.trashed} tone="danger" testId="stat-trashed" />
          </section>

          <section className="bg-white border border-border rounded-lg overflow-hidden">
            <div className="p-4 border-b border-border flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="font-heading text-lg text-primary">Médias orphelins détectés</div>
                <div className="text-xs text-muted-foreground">
                  Aperçu — aucun média n'est modifié tant que vous ne confirmez pas la mise en corbeille.
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={toggleAllOrphans}
                  disabled={orphans.length === 0}
                  data-testid="media-orphans-toggle-all"
                >
                  {selectedOrphans.size === orphans.length && orphans.length > 0
                    ? "Tout désélectionner"
                    : "Tout sélectionner"}
                </Button>
                <Button
                  onClick={() => setOpenMoveConfirm(true)}
                  disabled={!canMove || moving}
                  data-testid="media-trash-move-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Mettre en corbeille
                  {selectedOrphanCount > 0 ? ` (${selectedOrphanCount})` : ` (tous: ${orphans.length})`}
                </Button>
              </div>
            </div>

            {loadingAnalysis ? (
              <div className="p-12 flex items-center justify-center text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Analyse en cours…
              </div>
            ) : orphans.length === 0 ? (
              <div className="p-12 text-center text-sm text-muted-foreground">
                Aucun média orphelin détecté. Excellent&nbsp;: chaque média est référencé quelque part.
              </div>
            ) : (
              <table className="w-full text-sm" data-testid="media-orphans-table">
                <thead className="bg-secondary/50 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="text-left px-4 py-3 w-10"></th>
                    <th className="text-left px-4 py-3">Aperçu</th>
                    <th className="text-left px-4 py-3">ID</th>
                    <th className="text-left px-4 py-3">Type / Usage</th>
                    <th className="text-left px-4 py-3">Créé le</th>
                  </tr>
                </thead>
                <tbody>
                  {orphans.map((m) => {
                    const url = m.public_cloudinary_url || m.original_cloudinary_url;
                    return (
                      <tr
                        key={m.id}
                        className="border-t border-border"
                        data-testid={`orphan-row-${m.id}`}
                      >
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedOrphans.has(m.id)}
                            onChange={() => toggleOrphan(m.id)}
                            data-testid={`orphan-checkbox-${m.id}`}
                          />
                        </td>
                        <td className="px-4 py-3">
                          {url ? (
                            <img
                              src={url}
                              alt=""
                              className="w-14 h-14 object-cover rounded border border-border"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-14 h-14 rounded bg-muted" />
                          )}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                          {m.id?.slice(0, 8)}…
                        </td>
                        <td className="px-4 py-3 text-xs">
                          <div>{m.type || "media"}</div>
                          <div className="text-muted-foreground">
                            {m.usage_type ? `usage: ${m.usage_type}` : "sans usage défini"}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-muted-foreground">
                          {fmtDate(m.created_at)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            {orphansTotal > orphans.length && (
              <div className="p-3 text-xs text-muted-foreground border-t border-border">
                Affichage {orphans.length} sur {orphansTotal}
              </div>
            )}
          </section>
        </TabsContent>

        {/* -------- Corbeille -------- */}
        <TabsContent value="corbeille" className="mt-6 space-y-4">
          <section className="bg-white border border-border rounded-lg overflow-hidden">
            <div className="p-4 border-b border-border flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="font-heading text-lg text-primary">Médias en corbeille</div>
                <div className="text-xs text-muted-foreground">
                  Rétention par défaut&nbsp;: 30&nbsp;jours. Après expiration, la purge physique
                  Cloudinary reste manuelle et confirmée (dry-run par défaut).
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => doRestore(Array.from(selectedTrash))}
                  disabled={trashSelectionCount === 0}
                  data-testid="media-trash-restore-btn"
                >
                  <ArchiveRestore className="h-4 w-4 mr-2" />
                  Restaurer ({trashSelectionCount})
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={doPurgeDryRun}
                  disabled={trashSelectionCount === 0}
                  data-testid="media-trash-purge-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Purger ({trashEligibleCount} éligibles / {trashSelectionCount})
                </Button>
              </div>
            </div>

            {loadingTrash ? (
              <div className="p-12 flex items-center justify-center text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement…
              </div>
            ) : trashItems.length === 0 ? (
              <div className="p-12 text-center text-sm text-muted-foreground">
                La corbeille est vide.
              </div>
            ) : (
              <table className="w-full text-sm" data-testid="media-trash-table">
                <thead className="bg-secondary/50 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="text-left px-4 py-3 w-10"></th>
                    <th className="text-left px-4 py-3">Aperçu</th>
                    <th className="text-left px-4 py-3">ID</th>
                    <th className="text-left px-4 py-3">Mis en corbeille</th>
                    <th className="text-left px-4 py-3">Jours restants</th>
                    <th className="text-left px-4 py-3">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {trashItems.map((m) => {
                    const url = m.public_cloudinary_url || m.original_cloudinary_url;
                    return (
                      <tr key={m.id} className="border-t border-border" data-testid={`trash-row-${m.id}`}>
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedTrash.has(m.id)}
                            onChange={() => toggleTrash(m.id)}
                            data-testid={`trash-checkbox-${m.id}`}
                          />
                        </td>
                        <td className="px-4 py-3">
                          {url ? (
                            <img
                              src={url}
                              alt=""
                              className="w-14 h-14 object-cover rounded border border-border opacity-70"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-14 h-14 rounded bg-muted" />
                          )}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                          {m.id?.slice(0, 8)}…
                        </td>
                        <td className="px-4 py-3 text-xs text-muted-foreground">
                          {fmtDate(m.trashed_at)}
                        </td>
                        <td className="px-4 py-3 text-xs">
                          {m.days_remaining ?? "—"}
                        </td>
                        <td className="px-4 py-3 text-xs">
                          {m.purge_eligible ? (
                            <span className="inline-flex items-center gap-1 text-red-700 font-medium">
                              <AlertTriangle className="h-3.5 w-3.5" /> Purgeable
                            </span>
                          ) : (
                            <span className="text-muted-foreground">Rétention active</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </section>
        </TabsContent>
      </Tabs>

      {/* Confirm move-to-trash */}
      <Dialog open={openMoveConfirm} onOpenChange={setOpenMoveConfirm}>
        <DialogContent data-testid="media-trash-move-dialog">
          <DialogHeader>
            <DialogTitle>Confirmer la mise en corbeille</DialogTitle>
            <DialogDescription>
              {targetCount === 0
                ? "Aucun média orphelin à mettre en corbeille."
                : `Vous êtes sur le point de mettre en corbeille ${targetCount} média(s). Chaque média sera revérifié au moment de l'action (utilisation actuelle, archive sociale, déjà en corbeille) et refusé si nécessaire. Rétention par défaut : 30 jours. Aucun fichier n'est supprimé physiquement.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenMoveConfirm(false)} disabled={moving}>
              Annuler
            </Button>
            <Button
              onClick={doMoveToTrash}
              disabled={moving || targetCount === 0}
              data-testid="media-trash-move-confirm"
            >
              {moving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm purge (after dry-run) */}
      <Dialog open={openPurgeConfirm} onOpenChange={setOpenPurgeConfirm}>
        <DialogContent data-testid="media-trash-purge-dialog">
          <DialogHeader>
            <DialogTitle>Purge physique — dry-run</DialogTitle>
            <DialogDescription>
              Prévisualisation avant suppression définitive sur Cloudinary. Cette action est
              irréversible. La purge n'est autorisée que pour les médias dont la rétention
              (30&nbsp;jours) est expirée et qui ne sont plus référencés nulle part.
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm space-y-2">
            <div>
              <strong>Éligibles à la purge&nbsp;:</strong>{" "}
              <span data-testid="purge-eligible-count">{purgeResult?.eligible_count ?? 0}</span>
            </div>
            {(purgeResult?.refused || []).length > 0 && (
              <div>
                <strong>Refusés&nbsp;:</strong> {(purgeResult?.refused || []).length}
                <ul className="mt-2 text-xs text-muted-foreground list-disc list-inside max-h-40 overflow-auto">
                  {(purgeResult?.refused || []).map((r, i) => (
                    <li key={i}>
                      <code className="font-mono">{String(r.id).slice(0, 8)}…</code>{" "}
                      — {r.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenPurgeConfirm(false)} disabled={purging}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={doPurgeConfirmed}
              disabled={purging || !purgeResult || (purgeResult?.eligible_count ?? 0) === 0}
              data-testid="media-trash-purge-confirm"
            >
              {purging && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Confirmer la purge définitive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
